import { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { fetchCategories, fetchListings } from '../lib/api';
import { categoryInfo } from '../data/categoryFaqs';
import { applySeo, resetSeo } from '../lib/seo';
import { slugifyRegion } from '../lib/regions';
import ListingCard from '../components/ListingCard';
import JsonLd from '../components/JsonLd';
import type { Category, Listing } from '../types';

const BASE = 'https://marinelink.co.nz';

// Per-category copy hooks so region pages read naturally and target real search phrases
// (e.g. "bareboat charters Auckland", "fishing charters Bay of Islands").
const categoryKeywords: Record<string, { phrases: string; plural: string }> = {
  'boat-charters': {
    phrases: 'bareboat charters, skippered charters, harbour cruises and party boats',
    plural: 'boat charters',
  },
  'fishing-charters': {
    phrases: 'fishing charters, deep sea fishing trips and inshore fishing guides',
    plural: 'fishing charters',
  },
  'boat-rentals': {
    phrases: 'boat rentals, boat hire and self-drive rental boats',
    plural: 'boat rentals',
  },
  'marinas-berths': {
    phrases: 'marinas, boat berths, moorings and hardstand facilities',
    plural: 'marinas and berths',
  },
  'marine-chandlers': {
    phrases: 'marine chandlers, boating supplies and yacht chandlery',
    plural: 'marine chandlers',
  },
  'boat-builders-repair': {
    phrases: 'boat builders, boat repairs, refits and marine servicing',
    plural: 'boat builders and repairers',
  },
  'marine-electronics': {
    phrases: 'marine electronics, fishfinders, chartplotters and VHF radios',
    plural: 'marine electronics specialists',
  },
  'marine-engineering': {
    phrases: 'marine engineers, outboard servicing and diesel mechanics',
    plural: 'marine engineers',
  },
  'boats-for-sale': {
    phrases: 'boats for sale, yachts for sale and used boat listings',
    plural: 'boats for sale',
  },
  'fuel-bunkering': {
    phrases: 'marine fuel, diesel and bunkering services',
    plural: 'fuel suppliers',
  },
  'marine-insurance': {
    phrases: 'marine insurance, boat insurance and yacht insurance',
    plural: 'marine insurance providers',
  },
  'boat-ramps-launches': {
    phrases: 'boat ramps, launching facilities and trailer boat access',
    plural: 'boat ramps',
  },
  'sailmakers-canvas': {
    phrases: 'sailmakers, boat covers, biminis and marine canvas work',
    plural: 'sailmakers and canvas specialists',
  },
  'marine-services': {
    phrases: 'marine services, boat maintenance and industry support',
    plural: 'marine services',
  },
  'training-courses': {
    phrases: 'boating courses, skipper training and marine qualifications',
    plural: 'training providers',
  },
  'boat-transport': {
    phrases: 'boat transport, yacht delivery and boat hauling',
    plural: 'boat transport operators',
  },
  'diving-water-sports': {
    phrases: 'diving operators, snorkelling trips and water sports',
    plural: 'diving and water sports operators',
  },
};

export default function RegionCategoryLanding() {
  const { categorySlug, regionSlug } = useParams<{ categorySlug: string; regionSlug: string }>();
  const [category, setCategory] = useState<Category | null>(null);
  const [regionName, setRegionName] = useState<string | null>(null);
  const [listings, setListings] = useState<Listing[]>([]);
  const [otherRegions, setOtherRegions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const info = categorySlug ? categoryInfo[categorySlug] : undefined;
  const kw = categorySlug ? categoryKeywords[categorySlug] : undefined;

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetchCategories().then(async (cats) => {
      const cat = cats.find((c) => c.slug === categorySlug);
      if (!cat) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setCategory(cat);
      // Pull all listings in this category; resolve region from their region_name values
      const all = await fetchListings({ category: cat.id, sort: 'rating' });
      const regionNames = [...new Set(all.map((l) => l.region_name).filter(Boolean))] as string[];
      const match = regionNames.find((r) => slugifyRegion(r) === regionSlug);
      if (!match) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setRegionName(match);
      setListings(all.filter((l) => l.region_name === match));
      setOtherRegions(regionNames.filter((r) => r !== match).sort());
      setLoading(false);
    });
  }, [categorySlug, regionSlug]);

  useEffect(() => {
    if (category && regionName) {
      const plural = kw?.plural || category.name.toLowerCase();
      applySeo({
        title: `${category.name} in ${regionName} | MarineLink`,
        description: `Find ${listings.length || ''} ${plural} in ${regionName}. Compare ratings, services and contact details for ${kw?.phrases || plural} on MarineLink.`,
        descriptionPad: `Browse verified NZ marine businesses in ${regionName} and connect directly — no middleman, no booking fees.`,
        path: `/category/${category.slug}/${slugifyRegion(regionName)}`,
        keywords: `${plural} ${regionName}, ${category.name} ${regionName}, ${kw?.phrases || ''}, MarineLink`,
      });
    }
    return () => resetSeo();
  }, [category, regionName, listings.length, kw]);

  if (notFound) return <Navigate to={categorySlug ? `/category/${categorySlug}` : '/categories'} replace />;

  if (loading || !category || !regionName) {
    return <div className="text-center py-24 text-text-muted-light dark:text-text-muted">Loading...</div>;
  }

  const plural = kw?.plural || category.name.toLowerCase();

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `${category.name} in ${regionName}`,
    numberOfItems: listings.length,
    itemListElement: listings.slice(0, 25).map((l, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: l.title,
      url: `${BASE}/listing/${l.category_slug || category.slug}/${l.slug}`,
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE}/` },
      { '@type': 'ListItem', position: 2, name: 'Categories', item: `${BASE}/categories` },
      { '@type': 'ListItem', position: 3, name: category.name, item: `${BASE}/category/${category.slug}` },
      { '@type': 'ListItem', position: 4, name: regionName, item: `${BASE}/category/${category.slug}/${slugifyRegion(regionName)}` },
    ],
  };

  const faqSchema = info && info.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: info.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  } : null;

  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-8 py-8 md:py-12">
      <JsonLd id={`catregion-${category.slug}-${slugifyRegion(regionName)}`} data={[breadcrumbSchema, itemListSchema, ...(faqSchema ? [faqSchema] : [])]} />

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-muted-light dark:text-text-muted mb-6 flex-wrap">
        <Link to="/" className="text-text-secondary-light dark:text-text-secondary hover:text-accent transition-colors">Home</Link>
        <span>/</span>
        <Link to="/categories" className="text-text-secondary-light dark:text-text-secondary hover:text-accent transition-colors">Categories</Link>
        <span>/</span>
        <Link to={`/category/${category.slug}`} className="text-text-secondary-light dark:text-text-secondary hover:text-accent transition-colors">{category.name}</Link>
        <span>/</span>
        <span className="text-text-primary-light dark:text-text-primary">{regionName}</span>
      </div>

      {/* Hero */}
      <div className="mb-10 max-w-3xl">
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-text-primary-light dark:text-text-primary mb-4">
          {category.name} <span className="text-text-muted-light dark:text-text-muted font-medium">in {regionName}</span>
        </h1>
        <p className="text-text-secondary-light dark:text-text-secondary text-lg leading-relaxed">
          {kw
            ? `Looking for ${kw.phrases} in ${regionName}? MarineLink lists ${listings.length} ${plural} operating in the ${regionName} region — compare ratings, services and contact details, and get in touch directly.`
            : `Find ${plural} in ${regionName}. Browse ${listings.length} listings with ratings, services and contact details.`}
        </p>
        {info && (
          <p className="text-text-muted-light dark:text-text-muted mt-3 text-sm leading-relaxed">{info.description}</p>
        )}
        <p className="text-text-muted-light dark:text-text-muted mt-3 text-sm">
          {listings.length} listing{listings.length === 1 ? '' : 's'} in {regionName}
          {' · '}
          <Link to={`/category/${category.slug}`} className="text-accent hover:underline">All of New Zealand</Link>
        </p>
      </div>

      {/* Listings */}
      {listings.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 mb-16">
          {listings.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
      ) : (
        <div className="text-text-muted-light dark:text-text-muted mb-16">No listings in {regionName} yet.</div>
      )}

      {/* Other regions */}
      {otherRegions.length > 0 && (
        <section className="mb-16" aria-labelledby="other-regions-heading">
          <h2 id="other-regions-heading" className="font-display text-xl md:text-2xl font-bold text-text-primary-light dark:text-text-primary mb-4">
            {category.name} in other regions
          </h2>
          <div className="flex flex-wrap gap-2">
            {otherRegions.map((r) => (
              <Link
                key={r}
                to={`/category/${category.slug}/${slugifyRegion(r)}`}
                className="px-4 py-2 rounded-full border border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] text-sm text-text-secondary-light dark:text-text-secondary hover:border-accent hover:text-accent transition-colors"
              >
                {r}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {info && info.faqs.length > 0 && (
        <section className="max-w-3xl" aria-labelledby="faq-heading">
          <h2 id="faq-heading" className="font-display text-2xl md:text-3xl font-bold text-text-primary-light dark:text-text-primary mb-2">
            {category.name} in {regionName}: frequently asked questions
          </h2>
          <p className="text-text-muted-light dark:text-text-muted mb-6 text-sm">
            Expert answers about {plural} in {regionName} and across New Zealand.
          </p>
          <div className="space-y-3">
            {info.faqs.map((f) => (
              <details key={f.q} className="group border border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] rounded-lg bg-surface-light dark:bg-surface overflow-hidden">
                <summary className="cursor-pointer list-none px-5 py-4 font-medium text-text-primary-light dark:text-text-primary flex items-center justify-between gap-4 hover:text-accent transition-colors">
                  <span>{f.q}</span>
                  <span className="text-text-muted-light dark:text-text-muted group-open:rotate-45 transition-transform text-xl leading-none shrink-0">+</span>
                </summary>
                <div className="px-5 pb-5 text-text-secondary-light dark:text-text-secondary leading-relaxed border-t border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] pt-4">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
